/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import {
    Configuration,
    ConfigurationParameters,
    LicensePlate,
    LicensePlateApi,
    SamlToken,
    SamlTokenApi,
    SamlTokenApiSamlTokenPostRequest,
    SamlTokenRequest,
    LicensePlateApiLicensePlatePostRequest,
    LicensePlateRequest,
    DeviceBindingApiDeviceBindingDeleteRequest,
    DeviceBindingApi,
} from 'client-code/device-binding-service';
import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { DeviceModel } from '../device/DeviceModel';
import { IDeviceBindingServiceRepository } from '../interfaces/IDeviceBindingService';
import { Log } from '../logging/Log';
import { APPLICATION_FEATURE_AREA } from '../../config';

export class DeviceBindingServiceRepositoryImpl implements IDeviceBindingServiceRepository {
    TAG: string = 'DeviceBindingServiceRepositoryImpl';

    private static baseUrl: string;

    private commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.STARTUP).headers;

    private caDeviceId = DeviceModel.getCaDeviceId();

    static initialize(baseUrl: string) {
        DeviceBindingServiceRepositoryImpl.baseUrl = baseUrl;
    }

    private async fetchLicensePlatefromService(newLicensePlate: boolean): Promise<LicensePlate> {
        try {
            const configurationParameters: ConfigurationParameters = {
                basePath: DeviceBindingServiceRepositoryImpl.baseUrl,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: this.commonHeaders,
                },
            };

            const licensePlateRequest: LicensePlateRequest = {
                domain: DeviceModel.getTiVoSettings().getProvisioningDomain(),
                caDeviceId: this.caDeviceId,
                newLicensePlate,
            };

            const requestParameters: LicensePlateApiLicensePlatePostRequest = {
                applicationName: DeviceModel.getTiVoSettings().getAppName(),
                applicationFeatureArea: this.commonHeaders.ApplicationFeatureArea,
                applicationVersion: DeviceModel.getAppVersionName(),
                deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
                productName: DeviceModel.getTiVoSettings().getAppName(),
                contentType: this.commonHeaders['Content-Type'],
                licensePlateRequest,
            };

            const configuration = new Configuration(configurationParameters);
            const licensePlateService = new LicensePlateApi(configuration);
            const licensePlateApiResponse =
                await licensePlateService.licensePlatePost(requestParameters);

            return licensePlateApiResponse.data;
        } catch (error) {
            Log.e(this.TAG, 'fetchLicensePlatefromService', error);
            throw error;
        }
    }

    private async fetchSamlTokenfromService(licensePlateCode?: string): Promise<SamlToken> {
        try {
            const configurationParameters: ConfigurationParameters = {
                basePath: DeviceBindingServiceRepositoryImpl.baseUrl,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: this.commonHeaders,
                },
            };

            const samlTokenRequest: SamlTokenRequest = {
                caDeviceId: this.caDeviceId,
                domain: DeviceModel.getTiVoSettings().getProvisioningDomain(),
                licensePlateCode,
            };

            const requestParameters: SamlTokenApiSamlTokenPostRequest = {
                applicationName: DeviceModel.getTiVoSettings().getAppName(),
                applicationFeatureArea: this.commonHeaders.ApplicationFeatureArea,
                applicationVersion: DeviceModel.getAppVersionName(),
                deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
                productName: DeviceModel.getTiVoSettings().getAppName(),
                contentType: this.commonHeaders['Content-Type'],
                samlTokenRequest,
            };

            const configuration = new Configuration(configurationParameters);
            const samlTokenService = new SamlTokenApi(configuration);
            const samlTokenApiResponse = await samlTokenService.samlTokenPost(requestParameters);

            return samlTokenApiResponse.data;
        } catch (error) {
            Log.e(this.TAG, 'fetchSamlTokenfromService', error);
            throw error;
        }
    }

    private async removeDeviceBindingFromService(): Promise<void> {
        try {
            const configurationParameters: ConfigurationParameters = {
                apiKey: DeviceModel.getAccessToken(),
                basePath: DeviceBindingServiceRepositoryImpl.baseUrl,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: this.commonHeaders,
                },
            };

            const requestParameters: DeviceBindingApiDeviceBindingDeleteRequest = {
                applicationName: this.commonHeaders.ApplicationName,
                applicationFeatureArea: this.commonHeaders.ApplicationFeatureArea,
                applicationVersion: this.commonHeaders.ApplicationVersion,
                authorization: DeviceModel.getAccessToken(),
                bodyId: DeviceModel.getTiVoSettings().getBodyId(),
                deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
                productName: this.commonHeaders.ApplicationName,
                caDeviceId: this.caDeviceId,
                partnerId: DeviceModel.getTiVoSettings().getMsoPartnerId(),
                // Required as client does not do pr1ProvDeviceCancel and pr1ProvDeviceReset
                forceRemove: true,
            };

            const configuration = new Configuration(configurationParameters);
            const removeDeviceBindingService = new DeviceBindingApi(configuration);
            await removeDeviceBindingService.deviceBindingDelete(requestParameters);
        } catch (error) {
            Log.e(this.TAG, 'removeDeviceBindingFromService()', error);
            throw error;
        }
    }

    public async fetchLicensePlate(newLicensePlate: boolean): Promise<LicensePlate> {
        try {
            return await this.fetchLicensePlatefromService(newLicensePlate);
        } catch (error) {
            Log.e(this.TAG, 'fetchLicensePlate', error);
            throw error;
        }
    }

    public async fetchSamlToken(licensePlateCode?: string): Promise<SamlToken> {
        try {
            return await this.fetchSamlTokenfromService(licensePlateCode);
        } catch (error) {
            Log.e(this.TAG, 'fetchSamlToken', error);
            throw error;
        }
    }

    public async removeDeviceBinding(): Promise<void> {
        try {
            await this.removeDeviceBindingFromService();
        } catch (error) {
            Log.e(this.TAG, 'removeDeviceBinding()', error);
            throw error;
        }
    }
}
